// src/components/reels/ReelPlayer.js - EXACT PostCard Structure with Video
import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../utils/api';
import CommentList from '../comments/CommentList';
import CommentForm from '../comments/CommentForm';
import ReactionButtons from '../posts/ReactionButtons';
import '../../styles/post-card.css';

const ReelPlayer = ({ reel: post, showComments = false, onUpdate = null, onReact = null, onLike, onComment, onShare, onView, isActive }) => {
  const { user } = useAuth();
  const [expanded, setExpanded] = useState(showComments);
  const [isSharing, setIsSharing] = useState(false);
  const [shareText, setShareText] = useState('');
  const [comments, setComments] = useState([]);
  const [commentsLoaded, setCommentsLoaded] = useState(false);
  
  // Video specific state
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [showControls, setShowControls] = useState(true);
  const [hasViewed, setHasViewed] = useState(false);

  // Video effects
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);
    
    video.addEventListener('play', handlePlay);
    video.addEventListener('pause', handlePause);
    
    return () => {
      video.removeEventListener('play', handlePlay);
      video.removeEventListener('pause', handlePause);
    };
  }, []);

  useEffect(() => {
    if (isPlaying && !hasViewed && isActive && post) {
      setHasViewed(true);
      const reelId = post.id || post._id;
      if (reelId && onView) {
        onView(reelId);
      }
    }
  }, [isPlaying, hasViewed, isActive, onView, post?.id, post?._id]);

  // Early return for invalid post
  if (!post || typeof post !== 'object') {
    return null;
  }
  
  // Create a fallback author object if post.author is missing
  const author = post.author || { 
    username: post.username, 
    profilePic: null, 
    displayName: post.username 
  };
  
  // Format date
  const formattedDate = formatDistanceToNow(new Date(post.createdAt), { addSuffix: true });
  
  // Check if this is a shared post
  const isSharedPost = post.metadata && post.metadata.type === 'share';
  
  // Video functions
  const togglePlayPause = () => {
    const video = videoRef.current;
    if (!video) return;

    if (isPlaying) {
      video.pause();
    } else {
      video.play().catch(console.error);
    }
  };

  const toggleMute = () => {
    const video = videoRef.current;
    if (!video) return;

    video.muted = !video.muted;
    setIsMuted(video.muted);
  };

  const handleVideoClick = () => {
    togglePlayPause();
    setShowControls(true);
    setTimeout(() => setShowControls(false), 2000);
  };

  const getVideoUrl = (url) => {
    if (!url) return null;
    
    if (url.startsWith('ipfs://')) {
      const cid = url.replace('ipfs://', '');
      return `https://ipfs.io/ipfs/${cid}`;
    }
    
    return url;
  };

  const getIMGURL = (url) => {
    if (!url) return '/default-avatar.png';
    
    if (url.startsWith('ipfs://')) {
      const cid = url.replace('ipfs://', '');
      return `https://ipfs.io/ipfs/${cid}`;
    }
    
    return url;
  }
  
  // Load comments
  const loadComments = async () => {
    if (!commentsLoaded) {
      try {
        const response = await api.get(`/api/comments/post/${post.id}`);
        setComments(response.data.comments);
        setCommentsLoaded(true);
      } catch (error) {
        console.error('Error loading comments:', error);
      }
    }
  };
  
  // Toggle comments
  const toggleComments = () => {
    if (!expanded) {
      loadComments();
    }
    setExpanded(!expanded);
  };
  
  // Handle reaction
  const handleReaction = async (reactionType) => {
    if (onLike && reactionType === 'like') {
      const reelId = post.id || post._id;
      onLike(reelId);
    } else if (onReact) {
      onReact(reactionType);
    }
  };
  
  // Handle share
  const handleShare = async () => {
    try {
      const response = await api.post(`/api/posts/${post.id}/share`, { text: shareText });
      
      setIsSharing(false);
      setShareText('');
      
      if (onUpdate) {
        onUpdate();
      }
    } catch (error) {
      console.error('Error sharing post:', error);
    }
  };
  
  // Handle new comment
  const handleNewComment = (newComment) => {
    setComments([newComment, ...comments]);
    
    if (onUpdate) {
      onUpdate();
    }
  };

  const videoUrl = getVideoUrl(post.video?.url);
  
  return (
    <div className="post-card">
      {/* Post header - EXACT SAME as PostCard */}
      <div className="post-header">
        <Link to={`/profile/${author.username}`} className="author-link">
          <img 
            src={getIMGURL(author.profilePic) || '/default-avatar.png'} 
            alt={author.username}
            className="author-avatar" 
          />
          <div className="author-info">
            <h4 className="author-name">{author.displayName || author.username}</h4>
            <span className="post-date">{formattedDate}</span>
          </div>
        </Link>
      </div>
      
      {/* Post content */}
      <div className="post-content">
        <p className="post-text">{post.text}</p>
        
        {/* VIDEO INSTEAD OF IMAGE */}
        {videoUrl && (
          <div className="post-image-container" style={{ position: 'relative' }}>
            <video
              ref={videoRef}
              className="post-image"
              src={videoUrl}
              onClick={handleVideoClick}
              loop
              muted={isMuted}
              playsInline
              preload="metadata"
              poster={getVideoUrl(post.video?.thumbnail)}
              style={{ 
                width: '100%', 
                maxHeight: '500px', 
                objectFit: 'contain',
                backgroundColor: 'black',
                cursor: 'pointer'
              }}
            >
              <source src={videoUrl} type="video/mp4" />
            </video>

            {/* Video Controls Overlay */}
            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, pointerEvents: 'none' }}>
              
              {/* Play/Pause Overlay */}
              {showControls && !isPlaying && (
                <button
                  onClick={handleVideoClick}
                  style={{ 
                    position: 'absolute', 
                    top: '50%', 
                    left: '50%', 
                    transform: 'translate(-50%, -50%)',
                    backgroundColor: 'rgba(0,0,0,0.5)', 
                    border: 'none',
                    borderRadius: '50%', 
                    padding: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    pointerEvents: 'auto'
                  }}
                  type="button"
                >
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="white">
                    <path d="M8 5v14l11-7z"/>
                  </svg>
                </button>
              )}

              {/* Pause Overlay - Shows when playing */}
              {isPlaying && showControls && (
                <button
                  onClick={handleVideoClick}
                  style={{ 
                    position: 'absolute', 
                    top: '50%', 
                    left: '50%', 
                    transform: 'translate(-50%, -50%)',
                    backgroundColor: 'rgba(0,0,0,0.5)', 
                    border: 'none',
                    borderRadius: '50%', 
                    padding: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    pointerEvents: 'auto'
                  }}
                  type="button"
                >
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="white">
                    <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z"/>
                  </svg>
                </button>
              )}

              {/* Mute Button - Bottom Right */}
              <button
                onClick={(e) => {
                  e.stopPropagation(); // Prevent triggering video play/pause
                  toggleMute();
                }}
                style={{
                  position: 'absolute',
                  bottom: '12px',
                  right: '12px',
                  backgroundColor: 'rgba(0,0,0,0.6)',
                  border: 'none',
                  borderRadius: '50%',
                  padding: '8px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  pointerEvents: 'auto'
                }}
                type="button"
              >
                {isMuted ? (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
                    <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z"/>
                  </svg>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
                    <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/>
                  </svg>
                )}
              </button>
            </div>
          </div>
        )}
        
        {/* Shared post - SAME as PostCard */}
        {isSharedPost && post.sharedPost && (
          <div className="shared-post">
            <div className="shared-post-header">
              <span>Originally posted by {post.sharedPost.username}</span>
            </div>
            <div className="shared-post-content">
              <p>{post.sharedPost.text}</p>
              {post.sharedPost.video ? (
                <video
                  src={getVideoUrl(post.sharedPost.video?.url)}
                  className="post-image"
                  controls
                  style={{ width: '100%', maxHeight: '400px', objectFit: 'contain' }}
                />
              ) : post.sharedPost.image && (
                <div className="post-image-container">
                  <img 
                    src={post.sharedPost.image?.startsWith('ipfs://') 
                      ? `https://ipfs.io/ipfs/${post.sharedPost.image.replace('ipfs://', '')}` 
                      : post.sharedPost.image} 
                    alt="Shared post" 
                    className="post-image" 
                  />
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      
      {/* Post stats - EXACT SAME as PostCard */}
      <div className="post-stats">
        <span>{post.reactions?.likes || 0} likes</span>
        <span>{post.commentCount || 0} comments</span>
        <span>{post.reactions?.shares || 0} shares</span>
      </div>
      
      {/* Reaction buttons - EXACT SAME as PostCard */}
      <ReactionButtons 
        onLike={() => handleReaction('like')}
        onComment={toggleComments}
        onShare={() => setIsSharing(!isSharing)}
        isLiked={post.userReaction === 'like'}
      />
      
      {/* Share form - EXACT SAME as PostCard */}
      {isSharing && (
        <div className="share-form">
          <textarea
            placeholder="Add a comment to your share..."
            value={shareText}
            onChange={(e) => setShareText(e.target.value)}
          />
          <div className="share-actions">
            <button onClick={() => setIsSharing(false)}>Cancel</button>
            <button onClick={handleShare}>Share</button>
          </div>
        </div>
      )}
      
      {/* Comments - EXACT SAME as PostCard */}
      {expanded && (
        <div className="comments-section">
          <CommentForm postId={post.id} onCommentAdded={handleNewComment} />
          
          <CommentList comments={comments} loading={!commentsLoaded} />
          
          {comments.length > 0 && !showComments && (
            <Link to={`/post/${post.id}`} className="view-all-comments">
              View all comments
            </Link>
          )}
        </div>
      )}
    </div>
  );
};

export default ReelPlayer;